import { Observable, Subject, Subscription } from "rxjs"
import { UploadImpl } from "./UploadImpl"
import type { MediaListItemFragment } from "../graphql/types"
import { inject } from "vue"
import type { Ref } from "vue"
import { ApolloClient } from "@apollo/client/core"
import type { NormalizedCacheObject } from "@apollo/client/core"
import { ImportImpl } from "./ImportImpl"
import { getMediaLinkContext } from "./mediaLinkContext"

export enum UploadPhase {
  FAILED,
  PREPARING,
  CREATING,
  UPLOADING,
  COMPLETE,
}

export interface Upload {
  readonly name: string
  readonly file?: File
  readonly size: number
  readonly state: Observable<UploadState>
}

export interface UploadState {
  /**
   * The upload phase.
   */
  readonly phase: UploadPhase;

  /**
   * The progress from 0 (no progress) to 1 (complete).
   */
  readonly progress?: number;
  readonly bytes?: number

  readonly error?: string;

  readonly media?: MediaListItemFragment
}

export class Uploads {
  private uploads = new Subject<Upload>()

  constructor(
    private token: Ref<string | null>,
    private refreshToken: () => Promise<void>,
    private graphqlClient: ApolloClient<NormalizedCacheObject>,
  ) {
  }

  upload(file: File): Upload {
    const upload = new UploadImpl(file, this.token, this.refreshToken, this.graphqlClient, getMediaLinkContext())
    this.uploads.next(upload)
    return upload
  }

  /**
   * Ersetzt die Datei eines bestehenden Mediums. Bewusst ohne Link-Kontext — die
   * Verknuepfungen des Mediums bleiben serverseitig unangetastet.
   */
  replace(file: File, mediaUuid: string): Upload {
    return new UploadImpl(
      file,
      this.token,
      this.refreshToken,
      this.graphqlClient,
      { games: [], gameSeries: [], gameConsoles: [], companies: [], tags: [] },
      mediaUuid
    )
  }

  import(url: URL): Upload {
    const upload = new ImportImpl(url, this.graphqlClient, getMediaLinkContext())
    this.uploads.next(upload)
    return upload
  }

  subscribe(observer: (value: Upload) => void): Subscription {
    return this.uploads.subscribe(observer)
  }
}

export const UploadsKey = Symbol("Uploads")

export function useUploads(): Uploads {
  return inject(UploadsKey) as Uploads
}
